import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingUp, TrendingDown, Minus, Brain, Users, Activity, DollarSign, Clock, RefreshCw, Download } from 'lucide-react';
import { analyzeClosingOpportunity } from '../../../services/openai';

const LeadScoringPanel = ({ leads = [], onScoreUpdate }) => {
  const [scoringMetrics, setScoringMetrics] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('all');
  const [sortBy, setSortBy] = useState('score');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [aiInsights, setAiInsights] = useState({});

  useEffect(() => {
    initializeScoringMetrics();
  }, []);

  const initializeScoringMetrics = () => {
    // Define lead scoring criteria
    const metrics = [
      {
        id: 'demographic',
        name: 'Demographics',
        weight: 20,
        factors: [
          { name: 'Company Size', weight: 8 },
          { name: 'Industry Match', weight: 7 },
          { name: 'Location', weight: 3 },
          { name: 'Role/Title', weight: 2 }
        ]
      },
      {
        id: 'behavioral',
        name: 'Behavioral',
        weight: 35,
        factors: [
          { name: 'Email Opens', weight: 8 },
          { name: 'Link Clicks', weight: 10 },
          { name: 'Website Visits', weight: 12 },
          { name: 'Content Downloads', weight: 5 }
        ]
      },
      {
        id: 'engagement',
        name: 'Engagement',
        weight: 30,
        factors: [
          { name: 'Response Rate', weight: 12 },
          { name: 'Meeting Attendance', weight: 8 },
          { name: 'Demo Requests', weight: 10 }
        ]
      },
      {
        id: 'qualification',
        name: 'Qualification',
        weight: 15,
        factors: [
          { name: 'Budget Confirmed', weight: 6 },
          { name: 'Timeline Defined', weight: 4 },
          { name: 'Decision Authority', weight: 5 }
        ]
      }
    ];
    setScoringMetrics(metrics);
  };

  const calculateLeadScore = (lead) => {
    // Simulate lead scoring calculation based on various factors
    let score = 0;
    
    // Demographic scoring
    if (lead?.company && lead?.company?.length > 10) score += 8;
    if (lead?.tags?.includes('enterprise')) score += 7;
    if (lead?.tags?.includes('decision-maker')) score += 2;
    
    // Behavioral scoring (simulated)
    if (lead?.engagementLevel === 'very-high') score += 35;
    else if (lead?.engagementLevel === 'high') score += 28;
    else if (lead?.engagementLevel === 'medium') score += 20;
    else score += 10;
    
    // Engagement scoring
    if (lead?.status === 'hot') score += 30;
    else if (lead?.status === 'warm') score += 20;
    else score += 10;
    
    // Qualification scoring
    if (lead?.tags?.includes('budget-confirmed')) score += 6;
    if (lead?.tags?.includes('demo-completed')) score += 4;
    if (lead?.potentialValue > 20000) score += 5;
    
    return Math.min(100, Math.max(0, score));
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    if (score >= 40) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreTrend = (score) => {
    if (score >= 80) return { icon: TrendingUp, color: 'text-green-500', label: 'Rising' };
    if (score >= 60) return { icon: Minus, color: 'text-yellow-500', label: 'Stable' };
    return { icon: TrendingDown, color: 'text-red-500', label: 'Declining' };
  };

  const handleAnalyzeWithAI = async (lead) => {
    setLoading(true);
    try {
      const interactionHistory = [
        { contact: lead?.lastContact, type: 'email', outcome: 'opened' },
        { contact: lead?.lastContact, type: 'email', outcome: 'clicked' }
      ];
      
      const leadMetrics = {
        score: calculateLeadScore(lead),
        engagement: lead?.engagementLevel,
        potentialValue: lead?.potentialValue
      };

      const analysis = await analyzeClosingOpportunity(interactionHistory, leadMetrics);
      setAiInsights(prev => ({
        ...prev,
        [lead?.id]: analysis
      }));
    } catch (error) {
      console.error('Error analyzing lead with AI:', error);
    } finally {
      setLoading(false);
    }
  };

  const processedLeads = leads?.map(lead => ({
    ...lead,
    calculatedScore: calculateLeadScore(lead),
    scoreChange: Math.random() > 0.5 ? '+' : '-' + Math.floor(Math.random() * 10),
    lastScoreUpdate: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000)?.toISOString()
  }));

  const filteredLeads = processedLeads?.filter(lead => {
    if (selectedMetric === 'all') return true;
    
    switch (selectedMetric) {
      case 'high':
        return lead?.calculatedScore >= 80;
      case 'medium':
        return lead?.calculatedScore >= 60 && lead?.calculatedScore < 80;
      case 'low':
        return lead?.calculatedScore < 60;
      default:
        return true;
    }
  });

  const sortedLeads = [...filteredLeads]?.sort((a, b) => {
    let valueA = a?.[sortBy];
    let valueB = b?.[sortBy];
    
    if (sortBy === 'score') {
      valueA = a?.calculatedScore;
      valueB = b?.calculatedScore;
    }
    
    if (typeof valueA === 'string') {
      valueA = valueA?.toLowerCase();
      valueB = valueB?.toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return valueA > valueB ? 1 : -1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  });

  const scoreDistribution = {
    high: processedLeads?.filter(lead => lead?.calculatedScore >= 80)?.length,
    medium: processedLeads?.filter(lead => lead?.calculatedScore >= 60 && lead?.calculatedScore < 80)?.length,
    low: processedLeads?.filter(lead => lead?.calculatedScore < 60)?.length
  };

  return (
    <div className="space-y-6">
      {/* Score Distribution Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Leads</p>
              <p className="text-3xl font-bold text-gray-900">{processedLeads?.length}</p>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">High Score (80+)</p>
              <p className="text-3xl font-bold text-green-900">{scoreDistribution?.high}</p>
            </div>
            <Target className="h-8 w-8 text-green-600" />
          </div>
          <div className="mt-2 flex items-center text-sm text-green-600">
            <TrendingUp className="h-4 w-4 mr-1" />
            Ready to close
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Medium Score (60-79)</p>
              <p className="text-3xl font-bold text-yellow-900">{scoreDistribution?.medium}</p>
            </div>
            <Activity className="h-8 w-8 text-yellow-600" />
          </div>
          <div className="mt-2 flex items-center text-sm text-yellow-600">
            <Minus className="h-4 w-4 mr-1" />
            Nurture needed
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Low Score (&lt;60)</p>
              <p className="text-3xl font-bold text-red-900">{scoreDistribution?.low}</p>
            </div>
            <TrendingDown className="h-8 w-8 text-red-600" />
          </div>
          <div className="mt-2 flex items-center text-sm text-red-600">
            <Clock className="h-4 w-4 mr-1" />
            Long-term nurture
          </div>
        </motion.div>
      </div>
      {/* Scoring Methodology */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Brain className="h-5 w-5 mr-2 text-blue-600" />
            Lead Scoring Methodology
          </h3>
          <p className="text-sm text-gray-600 mt-1">AI-powered scoring based on demographics, behavior, engagement, and qualification</p>
        </div>
        
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {scoringMetrics?.map((metric) => (
            <div key={metric?.id} className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">{metric?.name}</h4>
                <span className="text-sm font-semibold text-blue-600">{metric?.weight}%</span>
              </div>
              <div className="space-y-2">
                {metric?.factors?.map((factor, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{factor?.name}</span>
                    <span className="text-gray-400">{factor?.weight}pts</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Filters and Controls */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedMetric('all')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedMetric === 'all' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Scores
          </button>
          <button
            onClick={() => setSelectedMetric('high')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedMetric === 'high' ?'bg-green-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            High (80+)
          </button>
          <button
            onClick={() => setSelectedMetric('medium')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedMetric === 'medium' ?'bg-yellow-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Medium (60-79)
          </button>
          <button
            onClick={() => setSelectedMetric('low')}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedMetric === 'low' ?'bg-red-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Low (&lt;60)
          </button>
        </div>
        
        <div className="flex gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="score">Sort by Score</option>
            <option value="name">Sort by Name</option>
            <option value="potentialValue">Sort by Value</option>
            <option value="lastContact">Sort by Last Contact</option>
          </select>
          
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
          
          <button className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center">
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </button>
          
          <button className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center">
            <Download className="h-4 w-4 mr-1" />
            Export
          </button>
        </div>
      </div>
      {/* Lead Scores Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trend
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Engagement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Potential Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Update
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedLeads?.map((lead) => {
                const trend = getScoreTrend(lead?.calculatedScore);
                const TrendIcon = trend?.icon;
                
                return (
                  <motion.tr
                    key={lead?.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{lead?.name}</div>
                          <div className="text-sm text-gray-500">{lead?.company}</div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {lead?.tags?.slice(0, 2)?.map((tag, index) => (
                              <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getScoreColor(lead?.calculatedScore)}`}>
                          {lead?.calculatedScore}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">({lead?.scoreChange})</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <TrendIcon className={`h-4 w-4 mr-1 ${trend?.color}`} />
                        <span className={`text-sm ${trend?.color}`}>{trend?.label}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Activity className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="text-sm text-gray-900 capitalize">{lead?.engagementLevel?.replace('-', ' ')}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <DollarSign className="h-4 w-4 mr-1 text-green-500" />
                        {lead?.potentialValue?.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(lead.lastScoreUpdate)?.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleAnalyzeWithAI(lead)}
                        disabled={loading}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <Brain className="h-4 w-4 mr-1" />
                        AI Analysis
                      </button>
                      
                      {aiInsights?.[lead?.id] && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                          <p><strong>Readiness:</strong> {aiInsights?.[lead?.id]?.readiness_score}%</p>
                          <p><strong>Timing:</strong> {aiInsights?.[lead?.id]?.optimal_timing?.recommended_timeframe}</p>
                          <p><strong>Strategy:</strong> {aiInsights?.[lead?.id]?.recommended_approach?.primary_strategy}</p>
                        </div>
                      )}
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LeadScoringPanel;